import { useObserver } from 'mobx-react-lite';
import React from 'react';
import 'react-sliding-pane/dist/react-sliding-pane.css';
import styled from 'styled-components/macro';
import rotate from '../keyframes/rotate';
import { useAddressSelectStore } from '../models/AddressSelect';
import { useMst } from '../models/WithWineState';
import AddressContainer from './AddressContainer';
import Button from './Button';
import CartItem from './CartItem';
import Modal from './Modal';
import { useCartStore } from '../models/Cart';
import { formatCurrency } from '../utils/formatCurrencyWithoutDecimals';
import { CartProps } from '../types/CartProps';
import { parseBoolean } from '../utils/parseBoolean';

const UnstyledCartV1: React.FC<CartProps> = ({ className, ...props }) => {
  const stateTree = useMst();
  const addressSelectStore = useAddressSelectStore();
  const state = useCartStore();

  return useObserver(() => (
    <Modal
      className={'ww-widget ww-cart ' + className}
      open={state.isOpen}
      onBackdropClick={state.hide}
      header={<h3 className="header">Cart</h3>}
      // eslint-disable-next-line react/no-children-prop
      children={
        <div
          className={
            'cart-items cart-price-state-' +
            (stateTree.session?.calculatePriceState ?? '')
          }
        >
          {state.items.map((val) => (
            <CartItem
              key={'ci_' + val.id}
              {...val}
              calculatingState={stateTree.session?.calculatePriceState}
            />
          ))}
          {state.items.length === 0 && (
            <p className="cart-empty">
              {props.emptytitle || 'Your cart is empty'}
            </p>
          )}
          {state.items.length !== 0 && (
            <>
              <div className="shipping-state sub-total">
                {addressSelectStore.hasAddressFieldSelection ? (
                  <div>
                    <label>Shipping to</label>
                    <AddressContainer selectmessage="Select location for shipping" />
                  </div>
                ) : (
                  <label>Shipping</label>
                )}
                <div className="shipping-fee sub-total-amount">
                  {typeof state.shippingFee === 'number'
                    ? `${formatCurrency(state.shippingFee, state.currencyCode, {
                        fractionDigits: 2,
                      })}`
                    : '$-.--'}
                </div>
              </div>
              {state.totalItemCount > 0 &&
                state
                  .getSurchargeAndTaxLineItems(
                    parseBoolean(props.groupsurchargesandtaxes, null),
                    parseBoolean(props.groupsurcharges, null),
                    props.surchargesgrouplabel,
                  )
                  .map((lineItem) => (
                    <div className="added-tax sub-total" key={lineItem.label}>
                      <label>{lineItem.label}</label>
                      <div className="added-tax-amount sub-total-amount">
                        {`${formatCurrency(lineItem.total, state.currencyCode, {
                          fractionDigits: 2,
                        })}`}
                      </div>
                    </div>
                  ))}
              {!state.errorMessage ? (
                state.totalItemCount > 0 && (
                  <div className="total">
                    <h3>Total</h3>
                    <h3 className="amount">
                      {formatCurrency(state.totalPrice, state.currencyCode, {
                        fractionDigits: 2,
                      })}
                      {typeof state.shippingFee === 'number' ? (
                        <></>
                      ) : (
                        <span className="amount-addon">Plus Shipping</span>
                      )}
                    </h3>
                  </div>
                )
              ) : (
                <div className={'error-message ' + state.errorCssClass}>
                  <span>{state.errorMessage}</span>
                </div>
              )}
            </>
          )}
        </div>
      }
      footer={
        <div className="actions">
          <Button onClick={() => state.clearCart()}>Empty Cart</Button>
          <Button onClick={state.hide}>
            Continue<span className="hide-sm"> Shopping</span>
          </Button>
          <Button
            primary
            solid
            onClick={state.checkout}
            disabled={!state.ableToCheckout}
          >
            Checkout{' '}
            <div className="checkout-icon">
              {stateTree.session?.calculatePriceState === 'pending' ? (
                <div className="spinner">
                  <i className="icon-spinner" />
                </div>
              ) : (
                <i className="icon-right-open" />
              )}
            </div>
          </Button>
        </div>
      }
    />
  ));
};

const CartV1 = styled(UnstyledCartV1)`
  .header {
    margin: 0;
    font-size: 1.17em;
  }

  .hide-sm {
    display: none;
  }

  .actions {
    display: flex;
    flex-direction: row;
    justify-content: center;
    align-items: stretch;
  }

  .actions > * {
    flex: 1;
    margin-right: 1em;
  }

  .actions > *:last-child {
    margin-right: 0;
  }

  .cart-empty {
    text-align: center;
    color: var(--gray-3);
    margin: 0;
    padding: 1.5em;
    background-color: var(--white);
  }

  .sub-total {
    padding: 0.25em 1em;
    background-color: var(--white);
    display: flex;
    align-items: center;
  }

  .sub-total > label,
  .sub-total > div {
    flex: 1 1 auto;
  }

  .sub-total label {
    font-size: 1em;
  }

  .sub-total .sub-total-amount {
    display: flex;
    flex: 0 0 100px;
    justify-content: flex-end;
    font-size: 1em;
  }

  .sub-total > label > span {
    margin: 0.25em 1em 0.25em 0;
    display: inline-block;
  }

  .cart-state {
    padding: 0.5em 1em;
    border-color: var(--gray-2);
    color: var(--gray-5);
    font-family: inherit;
    font-size: inherit;
  }

  .cart-items {
    display: flex;
    flex-direction: column;
  }

  .cart-items .item {
    margin-bottom: 0.15em;
  }

  .cart-items .shipping,
  .cart-items .total {
    background-color: var(--white);
    padding: 1em;
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
  }

  .cart-items .error-message {
    background-color: var(--warn-color);
    padding: 1em;
    display: flex;
    flex-direction: row;
    color: var(--white);
  }

  .cart-items .shipping h3,
  .cart-items .total h3 {
    margin: 0;
    font-size: 1.17em;
  }
  .cart-items .total .amount {
    text-align: right;
  }
  .cart-items .total .amount .amount-addon {
    display: block;
    font-weight: bold;
  }

  .spinner {
    overflow: hidden;
    width: 1em;
    height: 1em;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    margin: 0;
    animation: ${rotate} 2s linear infinite;
    opacity: 0.5;
  }

  .checkout-icon {
    display: none;
    margin-left: 0.25em;
  }

  .cart-price-state-pending .amount,
  .cart-price-state-pending .sub-total-amount {
    color: var(--gray-1);
    background-color: var(--gray-1);
  }

  @media (min-width: 569px) {
    .hide-sm {
      display: initial;
    }

    .checkout-icon {
      display: inline-block;
    }

    .actions {
      justify-content: flex-end;
      align-items: stretch;
    }

    .actions > * {
      flex: none;
    }
  }
`;

export default CartV1;
